import React, { createContext, useContext } from 'react';

export type ThemeName = 'darkWebex' | 'lightWebex';

interface IThemeContextValue {
  theme: ThemeName;
  colors: {
    backgroundPrimary: string;
    backgroundSecondary: string;
    backgroundSecondaryActive: string;
    outlineSecondary: string;
    textPrimary: string;
    textSecondary: string;
  };
}

const themeColorMap: Record<ThemeName, IThemeContextValue['colors']> = {
  lightWebex: {
    backgroundPrimary: 'var(--mds-color-theme-common-background-solid-light, #ffffff)',
    backgroundSecondary: 'var(--mds-color-theme-background-solid-secondary-normal, #f7f7f7)',
    backgroundSecondaryActive: 'var(--mds-color-theme-background-primary-active, #e8e8e8)',
    outlineSecondary: 'var(--mds-color-theme-outline-secondary-normal, #e0e0e0)',
    textPrimary: 'var(--mds-color-theme-text-primary-normal, #222222)',
    textSecondary: 'var(--mds-color-theme-text-secondary-normal, #666666)',
  },
  darkWebex: {
    backgroundPrimary: 'var(--mds-color-theme-common-background-solid-dark, #1a1a1a)',
    backgroundSecondary: 'var(--mds-color-theme-background-solid-secondary-normal, #2b2b2b)',
    backgroundSecondaryActive: 'var(--mds-color-theme-background-primary-active, #3a3a3a)',
    outlineSecondary: 'var(--mds-color-theme-outline-secondary-normal, #3a3a3a)',
    textPrimary: 'var(--mds-color-theme-text-primary-normal, #ffffff)',
    textSecondary: 'var(--mds-color-theme-text-secondary-normal, #999999)',
  },
};

const ThemeContext = createContext<IThemeContextValue | undefined>(undefined);

interface IThemeProviderProps {
  theme: ThemeName;
  children: React.ReactNode;
}

export const ThemeContextProvider: React.FC<IThemeProviderProps> = ({
  theme,
  children,
}) => {
  const value: IThemeContextValue = {
    theme,
    colors: themeColorMap[theme],
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): IThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    return {
      theme: 'lightWebex',
      colors: themeColorMap.lightWebex,
    };
  }
  return context;
};
